/**
 * useResponsive - Comprehensive Responsive Utilities
 *
 * Phase 4: Mobile Parity Verification
 *
 * Provides:
 * - Breakpoint detection (mobile/tablet/desktop)
 * - Window dimensions tracking
 * - Orientation detection
 * - Touch device detection
 * - Reduced motion preference
 *
 * Breakpoints (Tailwind defaults):
 * - sm: 640px
 * - md: 768px
 * - lg: 1024px
 * - xl: 1280px
 * - 2xl: 1536px
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

// ============================================
// Constants
// ============================================

export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
};

export const DEVICE_TYPES = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  DESKTOP: 'desktop',
};

// WCAG 2.1 touch target requirements
export const TOUCH_TARGET = {
  MIN_SIZE: 44, // pixels
  MIN_SPACING: 8, // pixels between targets
};

// ============================================
// useResponsive Hook
// ============================================

/**
 * Main responsive hook providing all device/viewport information
 */
export const useResponsive = () => {
  const [state, setState] = useState(() => getInitialState());

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setState(getViewportState());
    };

    const handleOrientationChange = () => {
      // Small delay to let orientation settle
      setTimeout(() => {
        setState(getViewportState());
      }, 100);
    };

    // Initial state
    setState(getViewportState());

    // Event listeners
    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('orientationchange', handleOrientationChange);

    // Media query listeners for preference changes
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleMotionChange = (e) => {
      setState((prev) => ({ ...prev, prefersReducedMotion: e.matches }));
    };

    const handleDarkModeChange = (e) => {
      setState((prev) => ({ ...prev, prefersDarkMode: e.matches }));
    };

    reducedMotionQuery.addEventListener('change', handleMotionChange);
    darkModeQuery.addEventListener('change', handleDarkModeChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
      reducedMotionQuery.removeEventListener('change', handleMotionChange);
      darkModeQuery.removeEventListener('change', handleDarkModeChange);
    };
  }, []);

  return state;
};

/**
 * Get initial state for SSR/test environments
 */
function getInitialState() {
  return {
    width: 0,
    height: 0,
    breakpoint: 'lg',
    deviceType: DEVICE_TYPES.DESKTOP,
    isMobile: false,
    isTablet: false,
    isDesktop: true,
    isTouch: false,
    orientation: 'landscape',
    isPortrait: false,
    isLandscape: true,
    prefersReducedMotion: false,
    prefersDarkMode: true,
    pixelRatio: 1,
  };
}

/**
 * Get current viewport state
 */
function getViewportState() {
  const width = window.innerWidth;
  const height = window.innerHeight;
  const breakpoint = getBreakpoint(width);
  const deviceType = getDeviceType(width);
  const orientation = width > height ? 'landscape' : 'portrait';

  return {
    width,
    height,
    breakpoint,
    deviceType,
    isMobile: deviceType === DEVICE_TYPES.MOBILE,
    isTablet: deviceType === DEVICE_TYPES.TABLET,
    isDesktop: deviceType === DEVICE_TYPES.DESKTOP,
    isTouch: isTouchDevice(),
    orientation,
    isPortrait: orientation === 'portrait',
    isLandscape: orientation === 'landscape',
    prefersReducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    prefersDarkMode: window.matchMedia('(prefers-color-scheme: dark)').matches,
    pixelRatio: window.devicePixelRatio || 1,
  };
}

/**
 * Get current breakpoint name
 */
function getBreakpoint(width) {
  if (width < BREAKPOINTS.sm) return 'xs';
  if (width < BREAKPOINTS.md) return 'sm';
  if (width < BREAKPOINTS.lg) return 'md';
  if (width < BREAKPOINTS.xl) return 'lg';
  if (width < BREAKPOINTS['2xl']) return 'xl';
  return '2xl';
}

/**
 * Get device type based on width
 */
function getDeviceType(width) {
  if (width < BREAKPOINTS.md) return DEVICE_TYPES.MOBILE;
  if (width < BREAKPOINTS.lg) return DEVICE_TYPES.TABLET;
  return DEVICE_TYPES.DESKTOP;
}

/**
 * Detect touch device
 */
function isTouchDevice() {
  if (typeof window === 'undefined') return false;
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator.msMaxTouchPoints && navigator.msMaxTouchPoints > 0)
  );
}

// ============================================
// useBreakpoint Hook
// ============================================

/**
 * Simple breakpoint matching hook
 *
 * @example
 * const isLargeScreen = useBreakpoint('lg'); // true if >= 1024px
 * const isSmallOrMedium = useBreakpoint('md', 'max'); // true if < 768px
 */
export const useBreakpoint = (breakpoint, direction = 'min') => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const bp = BREAKPOINTS[breakpoint] || parseInt(breakpoint, 10);
    const query = direction === 'min' ? `(min-width: ${bp}px)` : `(max-width: ${bp - 1}px)`;

    const mediaQuery = window.matchMedia(query);

    const handleChange = (e) => {
      setMatches(e.matches);
    };

    setMatches(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [breakpoint, direction]);

  return matches;
};

// ============================================
// useWindowSize Hook
// ============================================

/**
 * Simple window size hook
 */
export const useWindowSize = () => {
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return size;
};

// ============================================
// useOrientation Hook
// ============================================

/**
 * Orientation detection hook
 */
export const useOrientation = () => {
  const [orientation, setOrientation] = useState('landscape');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleChange = () => {
      const isPortrait = window.innerWidth < window.innerHeight;
      setOrientation(isPortrait ? 'portrait' : 'landscape');
    };

    handleChange();
    window.addEventListener('resize', handleChange, { passive: true });
    window.addEventListener('orientationchange', handleChange);

    return () => {
      window.removeEventListener('resize', handleChange);
      window.removeEventListener('orientationchange', handleChange);
    };
  }, []);

  return {
    orientation,
    isPortrait: orientation === 'portrait',
    isLandscape: orientation === 'landscape',
  };
};

// ============================================
// useIsMobile Hook
// ============================================

/**
 * Simple mobile detection hook
 */
export const useIsMobile = () => {
  return useBreakpoint('md', 'max');
};

// ============================================
// useIsTouch Hook
// ============================================

/**
 * Touch device detection hook
 */
export const useIsTouch = () => {
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    setIsTouch(isTouchDevice());
  }, []);

  return isTouch;
};

// ============================================
// usePrefersReducedMotion Hook
// ============================================

/**
 * Reduced motion preference hook
 */
export const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const handleChange = (e) => {
      setPrefersReducedMotion(e.matches);
    };

    setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  return prefersReducedMotion;
};

// ============================================
// Mobile Parity Utilities
// ============================================

/**
 * Check if an element meets touch target size requirements
 */
export const checkTouchTargetSize = (element) => {
  if (!element) return { valid: false, message: 'Element not found' };

  const rect = element.getBoundingClientRect();
  const width = rect.width;
  const height = rect.height;
  const minSize = TOUCH_TARGET.MIN_SIZE;

  const issues = [];

  if (width < minSize) {
    issues.push(`Width ${width.toFixed(0)}px is below minimum ${minSize}px`);
  }
  if (height < minSize) {
    issues.push(`Height ${height.toFixed(0)}px is below minimum ${minSize}px`);
  }

  return {
    valid: issues.length === 0,
    width,
    height,
    minRequired: minSize,
    issues,
  };
};

/**
 * Verify mobile parity for a container
 * Checks all interactive elements for touch target compliance
 */
export const verifyMobileParity = (container) => {
  if (!container) return { valid: true, issues: [] };

  const interactiveSelectors = [
    'button',
    'a',
    'input',
    'select',
    'textarea',
    '[role="button"]',
    '[role="link"]',
    '[role="checkbox"]',
    '[role="radio"]',
    '[tabindex]:not([tabindex="-1"])',
  ];

  const elements = container.querySelectorAll(interactiveSelectors.join(','));
  const issues = [];

  elements.forEach((element) => {
    const result = checkTouchTargetSize(element);
    if (!result.valid) {
      issues.push({
        element: element.tagName.toLowerCase(),
        id: element.id || null,
        className: element.className || null,
        text: element.textContent?.slice(0, 30) || null,
        issues: result.issues,
      });
    }
  });

  return {
    valid: issues.length === 0,
    totalChecked: elements.length,
    issueCount: issues.length,
    issues,
  };
};

/**
 * Get responsive class names based on current breakpoint
 */
export const getResponsiveClasses = (classMap, breakpoint) => {
  const orderedBreakpoints = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'];
  const currentIndex = orderedBreakpoints.indexOf(breakpoint);

  let classes = classMap.base || '';

  orderedBreakpoints.forEach((bp, index) => {
    if (index <= currentIndex && classMap[bp]) {
      classes = classMap[bp];
    }
  });

  return classes;
};

// ============================================
// Responsive Container Component Utilities
// ============================================

/**
 * Calculate responsive value based on breakpoint
 */
export const getResponsiveValue = (values, breakpoint) => {
  if (typeof values !== 'object') return values;

  const orderedBreakpoints = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'];
  const currentIndex = orderedBreakpoints.indexOf(breakpoint);

  let value = values.base ?? values.xs ?? values.sm ?? Object.values(values)[0];

  orderedBreakpoints.forEach((bp, index) => {
    if (index <= currentIndex && values[bp] !== undefined) {
      value = values[bp];
    }
  });

  return value;
};

export default useResponsive;
